type Constructor<T = object> = new (...args: unknown[]) => T
type Factory<T = unknown> = (...args: unknown[]) => T
type ServiceIdentifier = string | symbol

interface Binding<T = unknown> {
  type: 'constructor' | 'factory' | 'instance'
  target: Constructor<T> | Factory<T> | T
  singleton: boolean
  dependencies?: ServiceIdentifier[]
}

export class Container {
  private bindings = new Map<ServiceIdentifier, Binding>()
  private instances = new Map<ServiceIdentifier, unknown>()

  /**
   * Register a class constructor
   */
  register<T>(identifier: ServiceIdentifier, constructor: Constructor<T>, singleton = true): void {
    this.bindings.set(identifier, {
      type: 'constructor',
      target: constructor,
      singleton,
    })
  }

  /**
   * Register a factory function
   */
  registerFactory<T>(identifier: ServiceIdentifier, factory: Factory<T>, singleton = true): void {
    this.bindings.set(identifier, {
      type: 'factory',
      target: factory,
      singleton,
    })
  }

  /**
   * Register an instance
   */
  registerInstance<T>(identifier: ServiceIdentifier, instance: T): void {
    this.bindings.set(identifier, {
      type: 'instance',
      target: instance,
      singleton: true,
    })
    this.instances.set(identifier, instance)
  }

  /**
   * Resolve a service by identifier
   */
  resolve<T>(identifier: ServiceIdentifier): T {
    const binding = this.bindings.get(identifier)
    
    if (!binding) {
      throw new Error(`Service not registered: ${String(identifier)}`)
    }

    // Return cached instance for singletons
    if (binding.singleton && this.instances.has(identifier)) {
      return this.instances.get(identifier) as T
    }

    let instance: T

    switch (binding.type) {
      case 'instance': {
        instance = binding.target as T
        break
      }
        
      case 'constructor': {
        const Constructor = binding.target as Constructor<T>
        instance = new Constructor()
        break
      }
        
      case 'factory': {
        const factory = binding.target as Factory<T>
        instance = factory()
        break
      }
        
      default:
        throw new Error(`Unknown binding type`)
    }

    // Cache singleton instances
    if (binding.singleton) {
      this.instances.set(identifier, instance)
    }

    return instance
  }

  /**
   * Check if a service is registered
   */
  has(identifier: ServiceIdentifier): boolean {
    return this.bindings.has(identifier)
  }

  /**
   * Clear all registrations and instances
   */
  clear(): void {
    this.bindings.clear()
    this.instances.clear()
  }

  /**
   * Get all registered service identifiers
   */
  getRegistered(): ServiceIdentifier[] {
    return Array.from(this.bindings.keys())
  }
}

// Create singleton container instance
export const container = new Container()